import { useMemo } from "react";

import {
  ParticipantBid,
  PivotDirection,
  PivotOffer,
  PivotParticipant,
  PivotPayload,
  PivotStage,
  TableParticipant,
  TableRebiddingRow,
  UseRebiddingTableDataReturn,
} from "./types";
import { getStationName } from "./utils";

import { useGetPivotTable } from "@/common/api";
import { TARIFF_LABELS } from "@/common/model";

interface UseRebiddingTableDataProps {
  orderId: string;
  enabled: boolean;
}

// Type guard для проверки структуры PivotPayload
function isPivotPayload(value: unknown): value is PivotPayload {
  if (!value || typeof value !== "object") {
    return false;
  }
  const payload = value as Record<string, unknown>;
  return (
    (payload.directions === undefined || Array.isArray(payload.directions)) &&
    (payload.ownerStages === undefined || Array.isArray(payload.ownerStages)) &&
    (payload.sobStages === undefined || Array.isArray(payload.sobStages)) &&
    (payload.totalWagons === undefined || typeof payload.totalWagons === "number")
  );
}

export const useRebiddingTableData = ({
  orderId,
  enabled,
}: UseRebiddingTableDataProps): UseRebiddingTableDataReturn => {
  // Получаем данные для сводной таблицы по переторжке
  const { data: pivotTableData } = useGetPivotTable(
    {
      orderId: orderId,
    },
    {
      query: {
        enabled: !!orderId && enabled,
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const pivotPayload: PivotPayload | undefined = useMemo(() => {
    const payload = pivotTableData?.data?.payload;
    return isPivotPayload(payload) ? payload : undefined;
  }, [pivotTableData?.data?.payload]);

  const pivotDirections = useMemo(
    () => pivotPayload?.directions ?? [],
    [pivotPayload?.directions],
  );
  const ownerStages = useMemo(
    () => pivotPayload?.ownerStages ?? [],
    [pivotPayload?.ownerStages],
  );
  const sobStages = useMemo(
    () => pivotPayload?.sobStages ?? [],
    [pivotPayload?.sobStages],
  );

  // Преобразуем направления для PDF
  const tableRebiddingData = useMemo<TableRebiddingRow[]>(() => {
    return pivotDirections
      .map((direction: PivotDirection, index: number) => {
        const id = String(direction.index ?? index);

        const roadStation =
          direction.roadStation ??
          getStationName(direction.unloadingRoad, direction.unloadingStation) ??
          "-";

        const unit =
          direction.unit ??
          (direction.tariffRoad && TARIFF_LABELS[direction.tariffRoad]
            ? TARIFF_LABELS[direction.tariffRoad]
            : "-");

        const yourProposal =
          direction.yourProposal !== null && direction.yourProposal !== undefined
            ? String(direction.yourProposal)
            : undefined;

        return {
          id,
          roadStation,
          yourProposal,
          unit,
        };
      })
      .filter((row: TableRebiddingRow) => row.id);
  }, [pivotDirections]);

  // Формируем участников для PDF
  const tableParticipants = useMemo<TableParticipant[]>(() => {
    if (!Array.isArray(sobStages)) return [];

    return sobStages.map((participant: PivotParticipant, index: number) => {
      const bids: Record<string, ParticipantBid> = {};

      const stages = Array.isArray(participant.stages) ? participant.stages : [];

      let countWagonsOwner: number | undefined;

      stages.forEach((stage: PivotStage) => {
        const stageNumber = typeof stage.stage === "number" ? stage.stage : 0;
        const offers = Array.isArray(stage.offers) ? stage.offers : [];

        if (countWagonsOwner === undefined && typeof stage.countWagonsOwner === "number") {
          countWagonsOwner = stage.countWagonsOwner;
        }

        offers.forEach((offer: PivotOffer) => {
          const directionKey =
            offer.indexNumber !== undefined && offer.indexNumber !== null
              ? String(offer.indexNumber)
              : "";

          if (!directionKey) return;

          if (!bids[directionKey]) {
            bids[directionKey] = { initial: undefined, rebiddings: {} };
          }

          const bidForDirection = bids[directionKey];

          const value =
            offer.wagonTariff !== null && offer.wagonTariff !== undefined
              ? String(offer.wagonTariff)
              : undefined;

          if (!value) return;

          if (stageNumber === 0) {
            bidForDirection.initial = value;
          } else if (stageNumber > 0) {
            bidForDirection.rebiddings[stageNumber] = value;
          }
        });
      });

      return {
        id: String(participant.participantNumber ?? index + 1),
        name:
          participant.name ??
          (participant.participantNumber
            ? `Участник ${participant.participantNumber}`
            : `Участник ${index + 1}`),
        bids,
        countWagonsOwner,
      };
    });
  }, [sobStages]);

  // Определяем максимальное количество переторжек
  const maxRebiddingCount = useMemo(() => {
    const sobStagesMax =
      sobStages?.reduce((max: number, participant: PivotParticipant) => {
        const stages = Array.isArray(participant.stages) ? participant.stages : [];

        stages.forEach((s: PivotStage) => {
          const stageNum = typeof s.stage === "number" ? s.stage : 0;
          if (stageNum > 0) {
            max = Math.max(max, stageNum);
          }
        });

        return max;
      }, 0) ?? 0;

    const ownerStagesMax =
      ownerStages?.reduce((max: number, s: PivotStage) => {
        const stageNum = typeof s.stage === "number" ? s.stage : 0;
        if (stageNum > 0) {
          return Math.max(max, stageNum);
        }
        return max;
      }, 0) ?? 0;

    return Math.max(sobStagesMax, ownerStagesMax);
  }, [sobStages, ownerStages]);

  // Значения в колонке "Ваше предложение"
  const proposals = useMemo<Record<string, string>>(() => {
    const acc: Record<string, string> = {};

    tableRebiddingData.forEach((row: TableRebiddingRow) => {
      if (row.yourProposal) {
        acc[row.id] = row.yourProposal;
      }
    });

    if (ownerStages.length > 0) {
      const stagesWithOffers = ownerStages.filter(
        (s: PivotStage) => Array.isArray(s.offers) && s.offers.length > 0,
      );

      const stageToUse =
        stagesWithOffers.length > 0
          ? stagesWithOffers[stagesWithOffers.length - 1]
          : ownerStages[ownerStages.length - 1];

      const offers = Array.isArray(stageToUse.offers) ? stageToUse.offers : [];

      offers.forEach((offer: PivotOffer) => {
        if (
          offer.indexNumber === undefined ||
          offer.indexNumber === null ||
          offer.wagonTariff === undefined ||
          offer.wagonTariff === null
        ) {
          return;
        }

        const id = String(offer.indexNumber);
        acc[id] = String(offer.wagonTariff);
      });
    }

    return acc;
  }, [tableRebiddingData, ownerStages]);

  const totalWagonsRequired = useMemo(() => {
    for (const stage of ownerStages) {
      const offers = Array.isArray(stage.offers) ? stage.offers : [];

      if (offers.length > 0 && typeof stage.totalWagons === "number") {
        return stage.totalWagons;
      }
    }

    if (typeof pivotPayload?.totalWagons === "number") {
      return pivotPayload.totalWagons;
    }

    return "-";
  }, [ownerStages, pivotPayload?.totalWagons]);

  return {
    tableRebiddingData,
    tableParticipants,
    proposals,
    maxRebiddingCount,
    totalWagonsRequired,
  };
};
